import {
  WorksheetCoverageUnit,
  WorksheetDensityUnit,
  WorksheetHeaderUnit,
  WorksheetMaterialCalcMethod,
  WorksheetMaterialOutputUnit,
  WorksheetPricingMode,
  WorksheetThicknessUnit,
} from "generated/graphql";
import Decimal from "decimal.js";

export type WorksheetRowCalculation = {
  quantity?: number;
  totalHours?: number;
  costTotal: number;
};

export const roundCents = (value: Decimal.Value): number =>
  new Decimal(value).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();

export const roundQuantity = (value: Decimal.Value): number =>
  new Decimal(value).toDecimalPlaces(4, Decimal.ROUND_HALF_UP).toNumber();

export function calculateStandardRowCost(quantity: Decimal.Value, rate: Decimal.Value): number {
  return roundCents(new Decimal(quantity).times(rate));
}

export type TruckingCalculationInput = {
  truckingUnit?: string | null;
  trucksNeeded: Decimal.Value;
  hoursPerTruck: Decimal.Value;
  quantity?: Decimal.Value;
  rate: Decimal.Value;
};

export function calculateTruckingRow({
  truckingUnit,
  trucksNeeded,
  hoursPerTruck,
  quantity,
  rate,
}: TruckingCalculationInput): WorksheetRowCalculation {
  if (truckingUnit && truckingUnit !== "Hours") {
    return {
      costTotal: roundCents(new Decimal(quantity ?? 0).times(rate)),
    };
  }

  const totalHours = new Decimal(trucksNeeded).times(hoursPerTruck);
  return {
    totalHours: totalHours.toDecimalPlaces(4, Decimal.ROUND_HALF_UP).toNumber(),
    costTotal: roundCents(totalHours.times(rate)),
  };
}

export function applyPercentage(amount: Decimal.Value, percentage: Decimal.Value): number {
  return roundCents(new Decimal(amount).times(percentage).div(100));
}

export function calculateCostBasedTotal(
  directCosts: Decimal.Value,
  overheadPercentage: Decimal.Value,
  indirectPercentage: Decimal.Value,
  markupPercentage: Decimal.Value
): number {
  const direct = new Decimal(directCosts);
  return (
    roundCents(direct) +
    applyPercentage(direct, overheadPercentage) +
    applyPercentage(direct, indirectPercentage) +
    applyPercentage(direct, markupPercentage)
  );
}

export function calculateLineTotal({
  pricingMode,
  sellingQuantity,
  sellingRate,
  fixedTotal,
  directCosts,
  overheadPercentage,
  indirectPercentage,
  markupPercentage,
}: {
  pricingMode: WorksheetPricingMode;
  sellingQuantity: Decimal.Value;
  sellingRate: Decimal.Value;
  fixedTotal: Decimal.Value;
  directCosts: number | null;
  overheadPercentage: Decimal.Value;
  indirectPercentage: Decimal.Value;
  markupPercentage: Decimal.Value;
}): number {
  switch (pricingMode) {
    case WorksheetPricingMode.FIXED:
      return roundCents(fixedTotal);
    case WorksheetPricingMode.COST_BASED:
      return directCosts === null
        ? roundCents(fixedTotal)
        : calculateCostBasedTotal(
            directCosts,
            overheadPercentage,
            indirectPercentage,
            markupPercentage
          );
    case WorksheetPricingMode.RATE:
    default:
      return roundCents(new Decimal(sellingQuantity).times(sellingRate));
  }
}

export function calculateEnteredCosts(
  rowCosts: number[],
  overheadPercentage: Decimal.Value,
  indirectPercentage: Decimal.Value
): number | null {
  if (rowCosts.length === 0) return null;

  const direct = rowCosts.reduce((sum, cost) => sum + cost, 0);
  return (
    direct +
    applyPercentage(direct, overheadPercentage) +
    applyPercentage(direct, indirectPercentage)
  );
}

export function calculateEstimatedMargin(total: number, costs: number | null): number | null {
  if (costs === null || total <= 0) return null;
  return new Decimal(total)
    .minus(costs)
    .div(total)
    .times(100)
    .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
    .toNumber();
}

const SQ_FT_PER_SQ_YD = 9;
const POUNDS_PER_TON = 2000;

export function toSquareFeet(
  value: Decimal.Value,
  unit: WorksheetHeaderUnit | null | undefined
): Decimal | null {
  switch (unit) {
    case WorksheetHeaderUnit.SQUARE_FOOT:
      return new Decimal(value);
    case WorksheetHeaderUnit.SQUARE_YARD:
      return new Decimal(value).times(SQ_FT_PER_SQ_YD);
    default:
      return null;
  }
}

export function computeCoverageQuantity(
  jobSize: Decimal.Value,
  jobSizeUnit: WorksheetHeaderUnit | null | undefined,
  coverageRate: Decimal.Value,
  coverageUnit: WorksheetCoverageUnit | null | undefined
): Decimal | null {
  const rate = new Decimal(coverageRate);
  if (rate.lte(0)) return null;

  if (
    coverageUnit === WorksheetCoverageUnit.LINEAR_FOOT &&
    jobSizeUnit === WorksheetHeaderUnit.LINEAR_FOOT
  ) {
    return new Decimal(jobSize).div(rate);
  }

  const areaSqFt = toSquareFeet(jobSize, jobSizeUnit);
  if (areaSqFt === null) return null;

  const coverageSqFt =
    coverageUnit === WorksheetCoverageUnit.SQUARE_FOOT
      ? new Decimal(1)
      : coverageUnit === WorksheetCoverageUnit.SQUARE_YARD
      ? new Decimal(SQ_FT_PER_SQ_YD)
      : null;

  if (coverageSqFt === null) return null;

  return areaSqFt.div(coverageSqFt).div(rate);
}

export function convertThicknessToFeet(
  thickness: Decimal.Value,
  thicknessUnit: WorksheetThicknessUnit | null | undefined
): Decimal | null {
  switch (thicknessUnit) {
    case WorksheetThicknessUnit.INCH:
      return new Decimal(thickness).div(12);
    case WorksheetThicknessUnit.FOOT:
      return new Decimal(thickness);
    default:
      return null;
  }
}

export function convertPoundsToOutput(
  pounds: Decimal,
  outputUnit: WorksheetMaterialOutputUnit | null | undefined
): Decimal | null {
  switch (outputUnit) {
    case WorksheetMaterialOutputUnit.TON:
      return pounds.div(POUNDS_PER_TON);
    case WorksheetMaterialOutputUnit.POUND:
      return pounds;
    default:
      return null;
  }
}

export function computeDensityQuantity(
  jobSize: Decimal.Value,
  jobSizeUnit: WorksheetHeaderUnit | null | undefined,
  thickness: Decimal.Value,
  thicknessUnit: WorksheetThicknessUnit | null | undefined,
  density: Decimal.Value,
  outputUnit: WorksheetMaterialOutputUnit | null | undefined
): Decimal | null {
  const areaSqFt = toSquareFeet(jobSize, jobSizeUnit);
  const thicknessFt = convertThicknessToFeet(thickness, thicknessUnit);
  if (areaSqFt === null || thicknessFt === null) return null;

  const pounds = areaSqFt.times(thicknessFt).times(density);
  return convertPoundsToOutput(pounds, outputUnit);
}

export function computeThicknessFactorQuantity(
  jobSize: Decimal.Value,
  jobSizeUnit: WorksheetHeaderUnit | null | undefined,
  weightFactor: Decimal.Value,
  outputUnit: WorksheetMaterialOutputUnit | null | undefined
): Decimal | null {
  const areaSqFt = toSquareFeet(jobSize, jobSizeUnit);
  if (areaSqFt === null) return null;

  const pounds = areaSqFt.times(weightFactor);
  return convertPoundsToOutput(pounds, outputUnit);
}

export function computeMaterialQuantity({
  calcMethod,
  jobSize,
  jobSizeUnit,
  coverageRate,
  coverageUnit,
  thickness,
  thicknessUnit,
  density,
  densityUnit,
  weightFactor,
  outputUnit,
}: {
  calcMethod: WorksheetMaterialCalcMethod | null | undefined;
  jobSize: Decimal.Value;
  jobSizeUnit: WorksheetHeaderUnit | null | undefined;
  coverageRate: Decimal.Value;
  coverageUnit: WorksheetCoverageUnit | null | undefined;
  thickness: Decimal.Value;
  thicknessUnit: WorksheetThicknessUnit | null | undefined;
  density: Decimal.Value;
  densityUnit: WorksheetDensityUnit | null | undefined;
  weightFactor: Decimal.Value;
  outputUnit: WorksheetMaterialOutputUnit | null | undefined;
}): number | null {
  switch (calcMethod) {
    case WorksheetMaterialCalcMethod.COVERAGE: {
      const quantity = computeCoverageQuantity(jobSize, jobSizeUnit, coverageRate, coverageUnit);
      return quantity === null ? null : roundQuantity(quantity);
    }
    case WorksheetMaterialCalcMethod.DENSITY: {
      if (densityUnit && densityUnit !== WorksheetDensityUnit.LB_PER_CUFT) return null;
      const quantity = computeDensityQuantity(
        jobSize,
        jobSizeUnit,
        thickness,
        thicknessUnit,
        density,
        outputUnit
      );
      return quantity === null ? null : roundQuantity(quantity);
    }
    case WorksheetMaterialCalcMethod.THICKNESS_FACTOR: {
      const quantity = computeThicknessFactorQuantity(
        jobSize,
        jobSizeUnit,
        weightFactor,
        outputUnit
      );
      return quantity === null ? null : roundQuantity(quantity);
    }
    default:
      return null;
  }
}
